import { logger } from "@/utils/logger";
import { deleteCookie } from "@/utils/cookies";

/**
 * Session Management with Cookie-based Token Storage
 *
 * ✅ SECURITY IMPROVEMENTS:
 * - OAuth tokens now stored in secure cookies (not localStorage)
 * - Cookies have Secure flag (HTTPS only in production)
 * - SameSite=Strict for CSRF protection
 * - Automatic expiration based on token lifetime
 *
 * ⚠️ REMAINING RISK: Cookies are still JavaScript-accessible (not httpOnly)
 * - For maximum security, backend should set httpOnly cookies
 * - Current implementation is still vulnerable to XSS, but better than localStorage
 *
 * MITIGATION MEASURES:
 * - Ensure all user input is sanitized (DOMPurify for HTML)
 * - Implement Content Security Policy (CSP) headers
 * - Use short token expiration times
 * - Clear tokens immediately on logout
 * - Never log tokens to console or expose in error messages
 */

// ⚠️ DEPRECATED: Session timeout functionality has been removed
// Authentication is now token-based only (no idle timeout)
// These functions are kept for backwards compatibility but do nothing

/** @deprecated Session timeout removed - function does nothing */
export const SESSION_TIMEOUT = 10 * 60 * 1000;

/** @deprecated Session timeout removed - function does nothing */
export const resetSessionTimer = () => {
  // No-op: idle timeout functionality removed
};

/** @deprecated Session timeout removed - function does nothing */
export const getLastActivity = () => {
  return null;
};

/** @deprecated Session timeout removed - function does nothing */
export const getIdleTime = () => {
  return 0;
};

/** @deprecated Session timeout removed - function does nothing */
export const getRemainingIdleTime = () => {
  return 0;
};

/** @deprecated Session timeout removed - always returns false */
export const isSessionIdleExpired = () => {
  return false;
};

/**
 * Start a new session (call on login)
 *
 * ✅ SECURITY NOTE: OAuth tokens are now stored in secure cookies (see oauthService.ts)
 * User data (without sensitive tokens) is stored in localStorage for convenience.
 *
 * @param {Object} userData - User data (tokens should be stored separately via oauthService)
 */
export const startSession = (userData: Record<string, unknown>): void => {
  localStorage.setItem("session_start", Date.now().toString());
  localStorage.setItem("user", JSON.stringify(userData));
  logger.debug("[Session] Session started");
};

/**
 * Clear session and remove all session data
 *
 * SECURITY: Ensures all sensitive data is removed on logout.
 * - Clears user data from localStorage
 * - Clears OAuth tokens from cookies (via clearOAuthTokens in oauthService)
 * Should be called immediately on logout or session expiration.
 */
export const clearSession = () => {
  localStorage.removeItem("session_start");
  localStorage.removeItem("user");

  // Clear OAuth tokens from cookies
  // Note: clearOAuthTokens() should be called separately by auth store
  deleteCookie("oauth_access_token");
  deleteCookie("oauth_refresh_token");
  deleteCookie("oauth_id_token");
  deleteCookie("oauth_expires_at");

  logger.debug("[Session] Session cleared");
};

/**
 * Get user from session
 *
 * Returns user data from localStorage (without sensitive tokens).
 * OAuth tokens are stored separately in secure cookies.
 *
 * @returns {Object|null} User data or null if not found
 */
export const getUserFromSession = () => {
  try {
    const userData = localStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    logger.error("Error parsing user session data:", error);
    // Clear corrupted session data
    clearSession();
    return null;
  }
};

/**
 * Get authentication token from session
 *
 * ⚠️ SECURITY NOTE: Direct access to token. Use with caution.
 * Prefer using httpClient which handles token automatically.
 *
 * @returns {string|null} Authentication token or null
 */
export const getAuthToken = () => {
  const userData = getUserFromSession();
  return userData?.base64EncodedAuthenticationKey || null;
};

/** @deprecated Session timeout removed - always returns "0:00" */
export const getRemainingIdleTimeFormatted = () => {
  return "0:00";
};
